
import React from "react";
import { ChatHeader } from "./chat-header";
import MessageList from "./message-list";
import ChatInput from "./chat-input";
import { useChatMessages } from "./hooks/use-chat-messages";
import { useConversationTitle } from "./use-conversation-title";
import { useConversationDelete } from "./hooks/use-conversation-delete";
import ConversationDeleteDialog from "./conversation-delete-dialog";

interface ChatContainerProps {
  threadId: string | null;
  onThreadIdChange: (threadId: string | null) => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ threadId, onThreadIdChange }) => {
  const {
    messages,
    isLoading,
    apiError,
    hasPrivacyWarning,
    isProcessingLargeDataset,
    sendMessage,
    resetMessages
  } = useChatMessages(threadId, onThreadIdChange);

  const { conversationTitle } = useConversationTitle(threadId, "Ny samtale");

  const { 
    deleteDialogOpen, 
    conversationToDelete, 
    handleDeleteClick, 
    handleConfirmDelete, 
    closeDeleteDialog 
  } = useConversationDelete({ 
    currentThreadId: threadId, 
    onCreateNewConversation: () => onThreadIdChange(null),
    forceRefresh: () => {} // Not needed for active conversation
  });

  const handleDeleteConversation = () => {
    if (threadId) {
      // Create a minimal conversation object for the delete function
      const currentConversation = {
        id: threadId,
        thread_id: threadId,
        user_id: '',
        title: conversationTitle,
        last_message: '',
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        display_date: ''
      };
      handleDeleteClick(currentConversation, { stopPropagation: () => {} } as React.MouseEvent);
    } else {
      // If no threadId, just clear messages locally
      handleClearMessages();
    }
  };

  const handleClearMessages = () => {
    resetMessages();
    onThreadIdChange(null);
  };

  const handleNewChat = () => {
    resetMessages();
    onThreadIdChange(null);
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <ChatHeader 
        title={conversationTitle}
        onDelete={handleDeleteConversation}
        messages={messages}
        onNewChat={handleNewChat}
      />
      
      <div className="flex-1 overflow-hidden">
        <MessageList
          messages={messages}
          error={apiError}
          onClearMessages={handleDeleteConversation}
          hasPrivacyWarning={hasPrivacyWarning}
          isLoading={isLoading}
        />
      </div>
      
      <div className="border-t bg-background p-4">
        <ChatInput
          onSend={sendMessage}
          disabled={isLoading || isProcessingLargeDataset}
          isLoading={isLoading}
        />
      </div>

      <ConversationDeleteDialog
        isOpen={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ChatContainer;
